import type { v2 } from '@deque/billing-service-client';
import { ProductSlugs } from '@deque/billing-utils';
import {
  Checkbox,
  Icon,
  Loader,
  TextField,
  TooltipTabstop
} from '@deque/cauldron-react';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import PageTitle from '~/common/components/PageTitle';
import { useAxeAccountAnalytics } from '~/common/contexts/analytics';
import { useLoadedConfiguration } from '~/common/contexts/Configuration';
import { useFeatureFlagState } from '~/common/contexts/featureFlags';
import { useProducts } from '~/common/contexts/products';
import ImpactLabel from '../../../axe-watcher/components/issues/ImpactLabel';
import { type SeveritiesType } from '../../../axe-watcher/constants';
import styles from './Configuration.css';
import Form from './Form';
import RadioSetting from './RadioSetting';

const impacts: ReadonlyArray<SeveritiesType> = [
  'critical',
  'serious',
  'moderate',
  'minor'
];

enum IssuesIncluded {
  all_issues = 'all_issues',
  new_issues_only = 'new_issues_only'
}

interface AxeDeveloperConfigurationForm {
  onlyNewIssues: IssuesIncluded;
  impacts: {
    [key in SeveritiesType]: boolean;
  };
  sessionCloseTimeout: number;
}

interface AxeDeveloperHubConfigurationProps {
  product: v2.Product;
}

const AxeDeveloperHubConfiguration: React.FC<
  AxeDeveloperHubConfigurationProps
> = ({ product }) => {
  const { t } = useTranslation();
  const analytics = useAxeAccountAnalytics();
  const {
    settings,
    updateSettings,
    saving,
    isEnterpriseAdmin,
    isEnterpriseMember,
    updateSettingsShouldUpdateKey,
    loadingUserEnterpriseSettings
  } = useLoadedConfiguration();
  const { getProductBySlug } = useProducts();
  const { handleSubmit, reset, control, formState } =
    useForm<AxeDeveloperConfigurationForm>();
  const shouldShowSessionTimeout = useFeatureFlagState(
    'axe_devhub_sessionend_timeout'
  );
  const location = useLocation();

  const developerHubSettings = settings.axeDevtoolsWatcher;
  // Only allow editing / saving if the user is an enterprise admin
  const isReadOnly = isEnterpriseMember && !isEnterpriseAdmin;
  const disabled = saving || isReadOnly;

  React.useEffect(() => {
    if (developerHubSettings) {
      reset({
        onlyNewIssues: developerHubSettings.onlyNewIssues.value
          ? IssuesIncluded.new_issues_only
          : IssuesIncluded.all_issues,
        impacts: {
          critical: developerHubSettings.impacts.value.critical,
          serious: developerHubSettings.impacts.value.serious,
          moderate: developerHubSettings.impacts.value.moderate,
          minor: developerHubSettings.impacts.value.minor
        },
        sessionCloseTimeout: developerHubSettings.sessionCloseTimeout.value
      });
    }
  }, [developerHubSettings]);

  React.useEffect(() => {
    analytics.viewConfigurationProductPage(
      ProductSlugs.axeDevToolsWatcher,
      isEnterpriseAdmin
    );
  }, [isEnterpriseAdmin]);

  React.useEffect(() => {
    if (location.key) {
      updateSettingsShouldUpdateKey(location.key);
    }
  }, [location.key]);

  const onSubmit = (data: AxeDeveloperConfigurationForm) => {
    updateSettings({
      axeDevtoolsWatcher: {
        onlyNewIssues: {
          value: data.onlyNewIssues === IssuesIncluded.new_issues_only,
          type: 'default'
        },
        impacts: {
          value: {
            critical: data.impacts.critical,
            serious: data.impacts.serious,
            moderate: data.impacts.moderate,
            minor: data.impacts.minor
          },
          type: 'default'
        },
        sessionCloseTimeout: {
          // The server expects a number, but the form returns a string. We must coerce to avoid 400s from the API.
          value: parseInt(data.sessionCloseTimeout + '', 10),
          type: 'default'
        }
      }
    });
  };

  return (
    <>
      <PageTitle
        title={t('Configuration - {{productName}}', {
          productName: getProductBySlug(ProductSlugs.axeDevToolsWatcher)?.name
        })}
      />
      <Form
        header={product.name}
        isDirty={formState.isDirty}
        isReadOnly={isReadOnly}
        onSave={handleSubmit(onSubmit)}
        onDiscard={() => {
          reset();
        }}
      >
        <form>
          {loadingUserEnterpriseSettings ? (
            <Loader title={t('Loading Axe Developer Hub settings...')}></Loader>
          ) : (
            <>
              <div className={styles.headingContainer}>
                <h3>{t('Configure A11Y Threshold')}</h3>
              </div>
              <p>
                {t(
                  'You can determine the criteria used to calculate the A11Y Threshold. If the threshold is exceeded, it will cause the GitHub Actions and other integrations to treat the test suite as if it has failed.'
                )}
              </p>
              <fieldset>
                <RadioSetting
                  id="issues-included-label"
                  name="onlyNewIssues"
                  label={t('Issues Included')}
                  formControl={control}
                  disabled={disabled}
                  shouldShowAllowChange={false}
                  tooltip={t(
                    'Determine if all issues or new issues will be part of the A11Y Threshold total'
                  )}
                  options={[
                    {
                      id: IssuesIncluded.all_issues,
                      label: t('All Issues'),
                      value: IssuesIncluded.all_issues
                    },
                    {
                      id: IssuesIncluded.new_issues_only,
                      label: t('New Issues Only'),
                      value: IssuesIncluded.new_issues_only
                    }
                  ]}
                />
              </fieldset>

              <fieldset>
                <h4 className="Field__label" id="impact-label">
                  {t('Impacts Included')}
                </h4>
                <TooltipTabstop
                  tooltip={t(
                    'Choose the impacts that will contribute the A11Y Threshold'
                  )}
                  placement="top"
                  variant="info"
                >
                  <Icon
                    className={styles.headingTooltipIcon}
                    type="question-circle"
                    label={t('Impacts included help')}
                  />
                </TooltipTabstop>

                <div aria-labelledby="impact-label" role="group">
                  {impacts.map(impact => (
                    <Controller
                      key={impact}
                      control={control}
                      name={`impacts.${impact}`}
                      render={({ field: { value, onChange } }) => (
                        <Checkbox
                          onChange={onChange}
                          checked={value}
                          id={`impactIncluded-${impact}`}
                          label={<ImpactLabel impact={impact} />}
                          // Critical is always enabled and cannot be disabled
                          disabled={impact === 'critical' || disabled}
                        />
                      )}
                    />
                  ))}
                </div>
              </fieldset>

              {shouldShowSessionTimeout ? (
                <div data-testid="session-timeout">
                  <div className={styles.headingContainer}>
                    <h3>{t('Session Timeout')}</h3>
                  </div>
                  <p>
                    {t(
                      'By default, your project will stop accepting new data after 10 minutes since the last data came in. If you would like to override this, you can put your preferred timeout (in milliseconds) below. Once this timeout has been reached, the session will close and there will be NO way to add more data into that session.'
                    )}
                  </p>
                  <fieldset>
                    <Controller
                      control={control}
                      name="sessionCloseTimeout"
                      render={({ field: { value, onChange } }) => (
                        <TextField
                          label={t('Session Close Timeout (in ms)')}
                          name="sessionCloseTimeout"
                          type="number"
                          onChange={(_, event) => onChange(event)}
                          disabled={disabled}
                          value={value}
                        />
                      )}
                    />
                  </fieldset>
                </div>
              ) : null}
            </>
          )}
        </form>
      </Form>
    </>
  );
};

export default AxeDeveloperHubConfiguration;
